// Mock Data for Restaurant Analytics Dashboard

import {
  AnalyticsSummary,
  DailyOrderData,
  OrderCategoryData,
  RevenueData,
  RestaurantPerformance,
  AnalyticsMetric,
  TimeFilter,
  RestaurantFilter,
} from './types';

// Mock Analytics Summary
export const mockAnalyticsSummary: AnalyticsSummary = {
  totalOrders: 3130,
  totalRevenue: 430000,
  avgRating: 4.5,
  avgDeliveryTime: 23,
  totalRestaurants: 156,
  activeRestaurants: 134,
  topPerformingRestaurant: 'Tasty Chillies',
  revenueGrowth: 12.5,
  orderGrowth: 8.3,
};

// Mock Daily Order Data (Last 7 Days)
export const mockDailyOrderData: DailyOrderData[] = [
  { day: 'Mon', orders: 320, revenue: 45000 },
  { day: 'Tue', orders: 180, revenue: 25000 },
  { day: 'Wed', orders: 160, revenue: 22000 },
  { day: 'Thu', orders: 420, revenue: 58000 },
  { day: 'Fri', orders: 750, revenue: 105000 },
  { day: 'Sat', orders: 920, revenue: 128000 },
  { day: 'Sun', orders: 340, revenue: 47000 },
];

// Mock Order Category Data
export const mockOrderCategoryData: OrderCategoryData[] = [
  { category: 'Rice Dishes', percentage: 32, count: 1280, color: '#16a34a' },
  { category: 'Soups', percentage: 38, count: 1520, color: '#f59e0b' },
  { category: 'Pastries', percentage: 22, count: 880, color: '#8b5cf6' },
  { category: 'Nigerian', percentage: 10, count: 400, color: '#06b6d4' },
  { category: 'Drinks', percentage: 5, count: 200, color: '#3b82f6' },
];

// Mock Revenue Data (Last 7 weeks)
export const mockRevenueData: RevenueData[] = [
  { period: 'Week 1', revenue: 45000, orders: 180, averageOrderValue: 250 },
  { period: 'Week 2', revenue: 32000, orders: 128, averageOrderValue: 250 },
  { period: 'Week 3', revenue: 68000, orders: 272, averageOrderValue: 250 },
  { period: 'Week 4', revenue: 28000, orders: 112, averageOrderValue: 250 },
  { period: 'Week 5', revenue: 15000, orders: 60, averageOrderValue: 250 },
  { period: 'Week 6', revenue: 48000, orders: 192, averageOrderValue: 250 },
  { period: 'Week 7', revenue: 12000, orders: 48, averageOrderValue: 250 },
];

// Mock Restaurant Performance
export const mockRestaurantPerformance: RestaurantPerformance[] = [
  {
    restaurantId: 'rest-001',
    restaurantName: 'Tasty Chillies',
    totalOrders: 1250,
    totalRevenue: 312500,
    avgRating: 4.8,
    avgDeliveryTime: 25,
    orderTrend: 'up',
    revenueTrend: 'up',
  },
  {
    restaurantId: 'rest-002',
    restaurantName: 'The Place Restaurant',
    totalOrders: 950,
    totalRevenue: 237500,
    avgRating: 4.6,
    avgDeliveryTime: 30,
    orderTrend: 'up',
    revenueTrend: 'neutral',
  },
  {
    restaurantId: 'rest-003',
    restaurantName: 'Food Palace',
    totalOrders: 680,
    totalRevenue: 170000,
    avgRating: 4.2,
    avgDeliveryTime: 28,
    orderTrend: 'down',
    revenueTrend: 'down',
  },
  {
    restaurantId: 'rest-004',
    restaurantName: 'Ocean View Restaurant',
    totalOrders: 520,
    totalRevenue: 130000,
    avgRating: 4.4,
    avgDeliveryTime: 35,
    orderTrend: 'neutral',
    revenueTrend: 'up',
  },
];

// Mock Analytics Metrics
export const mockAnalyticsMetrics: AnalyticsMetric[] = [
  {
    id: 'total-orders',
    title: 'Total Orders',
    value: mockAnalyticsSummary.totalOrders,
    subtitle: 'This period',
    icon: '📦',
    color: 'green',
    trend: {
      direction: 'up',
      percentage: 12.5,
    },
  },
  {
    id: 'total-revenue',
    title: 'Total Revenue',
    value: `₦${mockAnalyticsSummary.totalRevenue}`,
    subtitle: 'This period',
    icon: '💰',
    color: 'blue',
    trend: {
      direction: 'up',
      percentage: 8.3,
    },
  },
  {
    id: 'avg-rating',
    title: 'Avg Rating',
    value: mockAnalyticsSummary.avgRating,
    subtitle: 'Overall rating',
    icon: '⭐',
    color: 'purple',
    trend: {
      direction: 'neutral',
      percentage: 0.1,
    },
  },
  {
    id: 'avg-delivery-time',
    title: 'Avg Delivery Time',
    value: `${mockAnalyticsSummary.avgDeliveryTime} Mins`,
    subtitle: 'Average time',
    icon: '🕐',
    color: 'orange',
    trend: {
      direction: 'down',
      percentage: 5.2,
    },
  },
];

// Filter Options
export const timeFilterOptions: TimeFilter[] = [
  { label: 'Last 7 Days', value: 'last_7_days', days: 7 },
  { label: 'Last 30 Days', value: 'last_30_days', days: 30 },
  { label: 'Last 3 Months', value: 'last_3_months', days: 90 },
  { label: 'Last 6 Months', value: 'last_6_months', days: 180 },
  { label: 'Last Year', value: 'last_year', days: 365 },
];

export const restaurantFilterOptions: RestaurantFilter[] = [
  { label: 'All Restaurants', value: 'all' },
  { label: 'Tasty Chillies', value: 'rest-001' },
  { label: 'The Place Restaurant', value: 'rest-002' },
  { label: 'Food Palace', value: 'rest-003' },
  { label: 'Ocean View Restaurant', value: 'rest-004' },
];

// Chart Colors
export const chartColors = {
  primary: '#16a34a',
  secondary: '#f59e0b',
  success: '#10b981',
  danger: '#ef4444',
  warning: '#f59e0b',
  info: '#3b82f6',
  light: '#f8fafc',
  dark: '#1e293b',
  purple: '#8b5cf6',
  pink: '#ec4899',
  indigo: '#6366f1',
  cyan: '#06b6d4',
  teal: '#14b8a6',
  lime: '#84cc16',
  orange: '#f97316',
  rose: '#f43f5e',
};

// Chart Configuration Presets
export const defaultChartConfig = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'bottom' as const,
    },
    tooltip: {
      enabled: true,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      titleColor: '#fff',
      bodyColor: '#fff',
    },
  },
};

export const barChartConfig = {
  ...defaultChartConfig,
  scales: {
    x: {
      display: true,
      grid: {
        display: false,
      },
    },
    y: {
      display: true,
      grid: {
        display: true,
      },
      beginAtZero: true,
    },
  },
};

export const lineChartConfig = {
  ...defaultChartConfig,
  scales: {
    x: {
      display: true,
      grid: {
        display: false,
      },
    },
    y: {
      display: true,
      grid: {
        display: true,
      },
      beginAtZero: true,
    },
  },
};

export const doughnutChartConfig = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'right' as const,
    },
    tooltip: {
      enabled: true,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      titleColor: '#fff',
      bodyColor: '#fff',
    },
  },
};

// Utility Functions
export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

export const formatNumber = (num: number): string => {
  return new Intl.NumberFormat('en-US').format(num);
};

export const formatPercentage = (value: number): string => {
  return `${value.toFixed(1)}%`;
};

export const getTrendIcon = (direction: 'up' | 'down' | 'neutral'): string => {
  switch (direction) {
    case 'up':
      return '📈';
    case 'down':
      return '📉';
    case 'neutral':
      return '➡️';
    default:
      return '➡️';
  }
};

export const getTrendColor = (direction: 'up' | 'down' | 'neutral'): string => {
  switch (direction) {
    case 'up':
      return 'text-green-600';
    case 'down':
      return 'text-red-600';
    case 'neutral':
      return 'text-gray-600';
    default:
      return 'text-gray-600';
  }
};

// Mock API delay simulation
export const delay = (ms: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

// Mock API response wrapper
export const mockApiResponse = <T>(data: T, delayMs: number = 500): Promise<T> => {
  return delay(delayMs).then(() => data);
};